import AdapterJalaali from "@date-io/jalaali";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import jMoment from "moment-jalaali";
import React, { useEffect, useReducer } from "react";
import { validate } from "../util/validators";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import {
	addCommas,
	digitsArToEn,
	digitsFaToEn,
	removeCommas,
} from "@persian-tools/persian-tools";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 250,
		},
	},
};

const inputReducer = (state, action) => {
	switch (action.type) {
		case "CHANGE":
			return {
				...state,
				value: action.val,
				isValid: validate(action.val, action.validators),
			};
		case "TOUCH": {
			return {
				...state,
				isTouched: true,
			};
		}
		default:
			return state;
	}
};

const Input = (props) => {
	const [inputState, dispatch] = useReducer(inputReducer, {
		value: props.initialValue || "",
		isTouched: false,
		isValid: props.inputValid || false, // triggers before anything happens
	});

	const { id, onInput } = props;
	const { value, isValid } = inputState;

	useEffect(() => {
		onInput(id, value, isValid);
	}, [id, value, isValid, onInput]);

	jMoment.loadPersian({ dialect: "persian-modern", usePersianDigits: true });

	const changeHandler = (event) => {
		dispatch({
			type: "CHANGE",
			val: event.target.value,
			validators: props.validators,
		});
	};

	const numberOnlyChangeHandler = (event) => {
		const value = event.target.value.replace(/[^\d,]/g, "");

		const digitsConverted = digitsArToEn(digitsFaToEn(value));
		dispatch({
			type: "CHANGE",
			val: props.needsThousandSeparator
				? removeCommas(digitsConverted).toString()
				: digitsConverted,
			validators: props.validators,
		});
	};

	const selectChangeHandler = (event) => {
		const {
			target: { value },
		} = event;
		dispatch({
			type: "CHANGE",
			val: typeof value === "string" ? value.split(",") : value,
			validators: props.validators,
		});
	};

	const autoCompleteChangeHandler = (event, value) => {
		dispatch({
			type: "CHANGE",
			val: value,
			validators: props.validators,
		});
	};
	const datePickChangeHandler = (date) => {
		dispatch({
			type: "CHANGE",
			val: date,
			validators: props.validators,
		});
	};

	const touchHandler = () => {
		dispatch({
			type: "TOUCH",
		});
	};

	let element = (
		<TextField
			error={!inputState.isValid && inputState.isTouched}
			id={props.id}
			type={props.type}
			label={props.label}
			variant={props.variant || "outlined"}
			size={props.size || "medium"}
			value={inputState.value}
			onBlur={touchHandler}
			onChange={changeHandler}
			helperText={
				!inputState.isValid && inputState.isTouched && props.errorText
			}
			fullWidth
			disabled={props.disabled || false}
			multiline={props.multiline}
			//required={props.required || false}
			inputProps={props.inputProps}
			margin={props.margin}
			//autoFocus
		/>
	);
	if (props.element === "number_only") {
		element = (
			<TextField
				error={!inputState.isValid && inputState.isTouched}
				id={props.id}
				type={props.type}
				label={props.label}
				variant={props.variant || "outlined"}
				size={props.size || "medium"}
				value={
					props.needsThousandSeparator && inputState.value !== ""
						? addCommas(removeCommas(inputState.value.toString()))
						: inputState.value
				}
				onBlur={touchHandler}
				onChange={(event) => {
					numberOnlyChangeHandler(event);
				}}
				helperText={
					!inputState.isValid &&
					inputState.isTouched &&
					props.errorText
				}
				fullWidth
				disabled={props.disabled || false}
				multiline={props.multiline}
				//required={props.required || false}
				inputProps={{
					pattern: props.needsThousandSeparator
						? "^\\d{1,3}(,\\d{3})*$"
						: "[0-9]*",
					...props.inputProps,
				}}
				margin={props.margin}
				//autoFocus
			/>
		);
	}
	if (props.element === "select") {
		element = (
			<div>
				<FormControl fullWidth>
					<InputLabel>{props.label}</InputLabel>
					<Select
						onBlur={touchHandler}
						id={props.id}
						multiple
						value={inputState.value}
						onChange={selectChangeHandler}
						input={<OutlinedInput label="عملیات" />}
						defaultValue={props.initialValue}
						renderValue={(selected) => (
							<Box
								sx={{
									display: "flex",
									flexWrap: "wrap",
									gap: 0.5,
								}}
							>
								{selected.map((value) => (
									<Chip
										key={value.stationName}
										label={value.stationName}
									/>
								))}
							</Box>
						)}
						MenuProps={MenuProps}
					>
						{props.options &&
							props.options.map((name) => (
								<MenuItem key={name.stationName} value={name}>
									{name.stationName}
								</MenuItem>
							))}
					</Select>
					<FormHelperText sx={{ color: "#f44336" }}>
						{!inputState.isValid &&
							inputState.isTouched &&
							props.errorText}
					</FormHelperText>
				</FormControl>
			</div>
		);
	}
	if (props.element === "autoComplete") {
		element = (
			<Autocomplete
				blurOnSelect="touch"
				noOptionsText="موردی یافت نشد"
				id={props.id}
				type={props.type}
				label={props.label}
				variant={props.variant || "outlined"}
				value={inputState.value}
				size={props.size || "medium"}
				defaultValue={props.initialValue}
				options={props.options}
				getOptionLabel={(option) =>
					!option ? "" : props.getOptionLabel(option)
				}
				// getOptionLabel={props.getOptionLabel}
				renderInput={(params) => (
					<TextField
						{...params}
						label={props.label}
						margin={props.margin}
						variant={props.variant || "outlined"}
						error={!inputState.isValid && inputState.isTouched}
						helperText={
							!inputState.isValid &&
							inputState.isTouched &&
							props.errorText
						}
					/>
				)}
				onBlur={touchHandler}
				onChange={autoCompleteChangeHandler}
				fullWidth
				disabled={props.disabled || false}
				multiline={props.multiline}
			/>
		);
	}
	if (props.element === "datePick") {
		element = (
			<FormControl fullWidth onBlur={touchHandler}>
				<LocalizationProvider dateAdapter={AdapterJalaali}>
					<DatePicker
						id={props.id}
						//inputFormat="jYYYY/jMM/jDD"
						value={inputState.value}
						onChange={datePickChangeHandler}
						disabled={props.disabled}
						disableFuture={props.disableFuture}
						disablePast={props.disablePast}
						label={props.label}
						margin={props.margin}
						disableMaskedInput
						renderInput={(params) => (
							<TextField
								{...params}
								fullWidth
								size={props.size || "medium"}
								variant={props.variant || "outlined"}
								error={props.error}
							/>
						)}
					/>
				</LocalizationProvider>
				<FormHelperText sx={{ color: "#f44336" }}>
					{!inputState.isValid &&
						inputState.isTouched &&
						props.errorText}
				</FormHelperText>
			</FormControl>
		);
	}

	return <div>{element}</div>;
};

export default Input;
